import React, { useCallback, useEffect, useRef, useState } from "react";
import { isDeviceOutputDesktop } from "../../../../Domain/DeviceOutputState";
import { usePostDetailsModalStore } from "./PostDetailsModalStore";
import { ModalConstructor } from "../ModalConstructor";
import {
    fetcherCommentDetails,
    mapServerResponseToComment,
} from "../../../DataFetchers/CommentDataFetcher";
import { Skeleton, Stack } from "@mui/material";
import { Box } from "@mui/system";
import { PostsWrapper } from "../../Post/PostsWrapper";
import {
    POST_LIST_TYPES,
    PostComponentOptionsType,
    PostGroupType,
    PostType,
} from "../../Post/Post.types";
import SkeletonPost from "../../Post/SkeletonPost";
import {
    CardHeaderPostHeader,
    PostCardDivider,
    TypographyHeaderSubTitle,
} from "../../Post/Post.styles";
import PinnedCommentsBlock from "../../Comment/PinnedCommentBlock";
import CommentsBlock from "../../Comment/CommentsBlock";
import { SearchFallback } from "../../Post/SearchFallback/SearchFallback";
import { PostFormModal } from "../../Post/PostForm/PostFormModal";
import { CommentFormModal } from "../CommentFormModal/CommentFormModal";
import {
    CommentContentType,
    CommentDataType,
    CommentSourceTypes,
    CommentType,
    ReplyContentType,
} from "../../Comment/CommentTypes";
import { usePostFormModalStore } from "../../../Pages/GroupDetailsPage/Stores/PostFormModalStore";
import {
    ErrorState,
    useErrorStore,
} from "../ServiceModals/GeneralErrorsModal/GlobalState";
import {
    CommentFormModeEnum,
    useCommentModalState,
} from "../CommentFormModal/CommentFormModalOpenState";
import { useReferencedState } from "@infrastructure/Hooks/useReferencedState";
import {
    fetcherGetPostDetails,
    updatePostFromServer,
} from "../../../DataFetchers/PostDataFetcher";
import { RESPONSE_CODE_RESOURCE_NOT_FOUND } from "@infrastructure/BackendClientCodes";
import { PostCommentsInfo } from "@infrastructure/Services/PostCommentsService.types";
import {
    POST_SOURCE_TYPE,
    POST_FORM_CONTENT_INPUT,
} from "../../Post/PostForm/PostFormModal.types";
import { LocationType } from "../../SearchHeader/ListItem/ListItemLocation.types";
import { CommentInput } from "../../Comment/CommentInput";
import { ReactJSXElement } from "@emotion/react/types/jsx-namespace";
import { usePostListEntryClickHandler } from "../../../Pages/GroupDetailsPage/Hooks/usePostDetailsNavigator";
import { ManualLoadingInfiniteScroller } from "../../Post/ManualLoadingInfiniteScroller";
import { useCommentsWithInfiniteScroll } from "../../Comment/useCommentsWithInfiniteScroll";
import { usePostListModalStore } from "../PostListModal/PostListModalStore";
import { RefreshContentButton } from "../../RefreshContentButton/RefreshContentButton";
import { SortingChips } from "../../SortingChips/SortingChips";
import { SortingChipsContainer } from "../../SortingChips/SortingChips.styles";
import { SortItemType } from "../../SortingChips/SortingChips.types";
import {
    getLocalSelectedCommentSortOption,
    setLocalSelectedCommentSortOption,
} from "@infrastructure/Persistence/LocalUserStorage";
import { SORT_CRITERIA_LIST } from "@shared/consts";
import { publish } from "@infrastructure/Events/EventBus";
import { POST_DETAILS_CHANGED } from "../../Post/PostForm/events";
import { POST_TYPES } from "@infrastructure/Services/PostService.types";
import { useCommentInputModalOpenCallback } from "../../Comment/useCommentInputModalOpenCallback";

export enum PostDetailsPageActions {
    REPLY = "reply",
    EDIT = "edit",
    PIN_COMMENT = "pinComment",
    WRITE_COMMENT = "writeComment",
    AUTO_PLAY_VIDEO = "autoPlayVideo",
}

const openCommentFormModal = useCommentModalState.getState().setIsOpen;
const SEARCH_FALLBACK_COMMENTS_COUNT = 3;

const BATCHES_LOADED_THRESHOLD = 4;

export function PostDetailsModal({
    fullScreen = false,
    insidePresenter = false,
}: {
    fullScreen?: boolean;
    insidePresenter?: boolean;
}) {
    const {
        selectedPostId,
        setIsPostDetailsModalOpen,
        isPostDetailsModalOpen,
        postDetailsAction,
        setPostDetailsAction,
    } = usePostDetailsModalStore();

    const [presenterIsOpened, setIsPresenterIsOpen] = useState(false);

    useEffect(() => {
        setIsPresenterIsOpen(insidePresenter);
    }, [insidePresenter]);

    const { setPostListAction } = usePostListModalStore();
    const { setIsPostFormModalOpen } = usePostFormModalStore();

    const handleClose = () => {
        setPostDetailsAction(undefined);
        setPostListAction(undefined);
        setIsPostFormModalOpen(false);
        setPost({} as PostComponentOptionsType);
        setPinnedComment(undefined);
        setPinnedCommentId(undefined);
        openCommentFormModal(false);
        setIsPresenterIsOpen(false);
        setIsPostDetailsModalOpen(false);
        setHeaderComponent(undefined);
    };

    const { setErrorState } = useErrorStore();
    const [pinnedComment, setPinnedComment] = useState<CommentType>();
    const [pinnedCommentScrolledTo, setPinnedCommentScrolledTo] =
        useState<boolean>();
    const onCommentSubmitScrollToRef = useRef<HTMLDivElement>(null);

    const [pinnedCommentId, setPinnedCommentId] = useState<number>();
    const pinnedCommentRef = useRef<HTMLDivElement>(null);

    /**
     * fake input needed to keep focus in iOS while rendering
     */
    const focusCatcherInputRef = useRef<HTMLInputElement>(null);
    const { openCommentInputModal } =
        useCommentInputModalOpenCallback(focusCatcherInputRef);
    const [selectedPost, setSelectedPost] = useState<PostComponentOptionsType>(
        {} as PostComponentOptionsType,
    );
    const [selectedComment, setSelectedComment] = useReferencedState(
        {} as CommentDataType,
    );

    const [replyComment, setReplyComment] = useState<ReplyContentType>(
        {} as CommentContentType,
    );

    const [post, setPost] = useState({} as PostComponentOptionsType);

    const [isSearchFallbackVisible, setIsSearchFallbackVisible] =
        useState(false);

    const updatePostComponent = useCallback(
        (postFetched: PostType) => {
            const callbacks = {
                onCommentButtonClick: openCommentInputModal,
            };

            const postComponent: PostComponentOptionsType = {
                id: postFetched.id,
                data: postFetched,
                postActions: callbacks,
            };

            setPost(postComponent);
            setPostGroup(postComponent?.data?.group);

            return postFetched;
        },
        [openCommentInputModal],
    );

    function updatePinnedComment(newPinnedComment: CommentType) {
        setPinnedComment(newPinnedComment);
    }

    const initialSelectedSortCriteriaIndex =
        getLocalSelectedCommentSortOption();

    const [sortingItemIndex, setSortingItemIndex] = useState(
        initialSelectedSortCriteriaIndex,
    );
    const [sortCriteria, setSortCriteria] = useState(
        SORT_CRITERIA_LIST[initialSelectedSortCriteriaIndex].criterion,
    );

    const onSortingItemChange = (sortItem: SortItemType) => {
        const index = SORT_CRITERIA_LIST.indexOf(sortItem);
        setLocalSelectedCommentSortOption(index);
        setSortingItemIndex(index);
        setSortCriteria(SORT_CRITERIA_LIST[index].criterion);
    };

    const [shouldRefresh, setShouldRefresh] = useState(false);
    useEffect(() => {
        setSortCriteria(
            SORT_CRITERIA_LIST[initialSelectedSortCriteriaIndex].criterion,
        );
        setSortingItemIndex(initialSelectedSortCriteriaIndex);
        if (!selectedPostId) {
            return;
        }

        if (shouldRefresh) {
            setPost({} as PostComponentOptionsType);
            reInitiate();
            setShouldRefresh(false);
            return;
        }

        fetcherGetPostDetails(
            selectedPostId.toString(),
            {
                showCommentButton: false,
                wrapperMarginBottomValue: "16px",
            },
            {
                sortCriteria: {
                    increaseCount: true,
                },
            },
            true,
        )
            .then((data: PostType) => {
                updatePostComponent(data);
            })
            .catch((error) => {
                if (
                    error.response?.status === RESPONSE_CODE_RESOURCE_NOT_FOUND
                ) {
                    setErrorState(ErrorState.POST_NOT_FOUND);
                    handleClose();
                } else {
                    setErrorState(ErrorState.GENERAL_ERROR);
                    handleClose();
                }
            });
    }, [selectedPostId, presenterIsOpened, shouldRefresh]);

    useEffect(() => {
        return () => {
            setPost({} as PostComponentOptionsType);
            setPinnedComment(undefined);
            openCommentFormModal(false);
            setIsPostDetailsModalOpen(false);
            setHeaderComponent(undefined);
        };
    }, []);

    useEffect(() => {
        !isPostDetailsModalOpen && setPost({} as PostComponentOptionsType);
    }, [isPostDetailsModalOpen]);

    const scrollableContainerRef = useRef<HTMLDivElement>();

    const {
        comments,
        deleteComment,
        updateComment,
        loadMore,
        loading,
        hasNextPage,
        error,
        infiniteRef,
        isInitDone,
        batchesLoaded,
        reInitiate,
    } = useCommentsWithInfiniteScroll({
        postId: selectedPostId,
        sortCriteria,
        scrollableContainerRef,
    });

    useEffect(() => {
        setIsSearchFallbackVisible(
            comments.length <= SEARCH_FALLBACK_COMMENTS_COUNT,
        );
    }, [selectedPostId, sortCriteria, comments.length]);

    useEffect(() => {
        setPinnedCommentId(undefined);
        setPinnedComment(undefined);
    }, [sortCriteria["orderBy[field]"]]);

    const [newCommentAdded, setNewCommentAdded] = useState<boolean>();
    useEffect(() => {
        if (newCommentAdded) {
            reInitiate();
        }
    }, [newCommentAdded]);
    useEffect(() => {
        if (pinnedCommentId) {
            fetcherCommentDetails(pinnedCommentId).then(
                (commentData: PostCommentsInfo) => {
                    setPinnedComment(mapServerResponseToComment(commentData));
                    setPinnedCommentScrolledTo(false);
                    setNewCommentAdded(false);
                },
            );
        }
    }, [pinnedCommentId]);

    useEffect(() => {
        if (!pinnedComment || loading || pinnedCommentScrolledTo) {
            return;
        }
        setTimeout(() => {
            pinnedCommentRef.current?.scrollIntoView({
                block: "start",
                behavior: "smooth",
            });
            setPinnedCommentScrolledTo(true);
        }, 400);
    }, [loading, pinnedComment]);

    const onPostEdit = () => {
        const _post = { ...post };
        setIsPostFormModalOpen(
            true,
            post.data.postData.type === POST_TYPES.POLL
                ? POST_FORM_CONTENT_INPUT.POLL_INPUT
                : POST_FORM_CONTENT_INPUT.DEFAULT,
        );
        setSelectedPost(_post);
    };

    function openPostFormHandler(sourceType?: POST_SOURCE_TYPE) {
        setIsPostFormModalOpen(
            true,
            POST_FORM_CONTENT_INPUT.DEFAULT,
            sourceType,
        );
    }

    function postFormModalCloseCallback() {
        setSelectedPost({} as PostComponentOptionsType);
    }

    const redirectToPostHandler = usePostListEntryClickHandler();
    const onPostFormSubmit = (newPostData: PostType, response) => {
        if (newPostData) {
            const id = newPostData.id.toString();
            fetcherGetPostDetails(id)
                .then((post) => {
                    updatePostComponent(post);
                })
                .catch((err) => {
                    console.error(err);
                });
        } else {
            redirectToPostHandler(response.id);
        }
    };

    const onCommentFormSubmit = useCallback(
        (update: PostCommentsInfo) => {
            const comment = comments.find(
                (comment) => comment.commentContent.id === update.id,
            );

            if (comment && comment.id !== pinnedCommentId) {
                updateComment(
                    "id",
                    update.id,
                    mapServerResponseToComment(update),
                );
            } else {
                setPinnedComment(mapServerResponseToComment(update));
                setPinnedCommentId(update.id);
                setNewCommentAdded(true);
            }
            fetcherGetPostDetails(post.id.toString())
                .then(updatePostComponent)
                .then((postFetched) => {
                    publish({
                        name: POST_DETAILS_CHANGED,
                        data: postFetched,
                    });
                });
        },
        [
            comments,
            pinnedCommentId,
            post.id,
            updateComment,
            updatePostComponent,
        ],
    );

    const clearReplyComment = useCallback(
        () => setReplyComment({} as CommentContentType),
        [],
    );

    const clearEditedComment = useCallback(
        () => setSelectedComment({} as CommentContentType),
        [setSelectedComment],
    );

    const onDeleteSuspendCommentSuccess = (
        commentResponse: PostCommentsInfo,
    ) => {
        const _comments = [...comments];
        const index = _comments.findIndex(
            (item) => item.commentContent.id === commentResponse.id,
        );
        if (index !== -1) {
            if (commentResponse.isSuspended) {
                updateComment(
                    "id",
                    _comments[index].commentContent.id,
                    mapServerResponseToComment(commentResponse),
                );
            }

            if (commentResponse.isDeleted) {
                deleteComment("id", _comments[index].commentContent.id);
                const _post = JSON.parse(JSON.stringify(post));
                _post.data.postData.commentsCount =
                    post.data.postData.commentsCount - 1;
                setPost(_post);
                if (commentResponse.id === pinnedCommentId) {
                    setPinnedComment(undefined);
                    setPinnedCommentId(undefined);
                }
            }
        }
    };

    function onReplyToComment(comment: ReplyContentType) {
        setReplyComment(comment);
        openCommentInputModal();
    }

    const onRepliedCommentClick = (id: number) => {
        setPinnedCommentId(id);
    };

    const onCommentEdit = (commentToEdit: CommentType) => {
        setSelectedComment({
            id: commentToEdit.commentContent.id,
            images: commentToEdit.commentContent.images,
            linkoutUrl: commentToEdit.commentContent.linkoutUrl,
            content: commentToEdit.commentContent.text,
        });
        if (commentToEdit.commentReply) {
            setReplyComment(commentToEdit.commentReply);
        }
        openCommentFormModal(true);
    };

    useEffect(() => {
        const actionEvent = () => {
            setTimeout(() => {
                isDeviceOutputDesktop()
                    ? openCommentInputModal(CommentFormModeEnum.TEXT, false)
                    : openCommentInputModal(CommentFormModeEnum.DEFAULT);
            }, 200);
        };

        if (
            postDetailsAction?.desiredAction ===
            PostDetailsPageActions.WRITE_COMMENT
        ) {
            actionEvent();
            setPostDetailsAction(undefined);
            return;
        }

        const commentId = postDetailsAction?.commentId;
        if (!comments || !commentId) {
            return;
        }

        const wantedComment = comments?.find(
            (comment) => comment.commentContent.id.toString() === commentId,
        );

        if (!wantedComment) {
            return;
        }
        switch (postDetailsAction.desiredAction) {
            case PostDetailsPageActions.REPLY:
                setReplyComment(wantedComment.commentContent);
                actionEvent();
                break;
            case PostDetailsPageActions.EDIT:
                onCommentEdit(wantedComment);
                break;
            case PostDetailsPageActions.PIN_COMMENT:
                setPinnedCommentId(wantedComment.commentContent.id);
                break;
        }
        setPostDetailsAction(undefined);
    }, [comments, postDetailsAction?.desiredAction]);

    function updateComments(newComment: CommentType) {
        updateComment(
            "id",
            newComment.commentContent.id,
            {} as CommentType,
            newComment,
        );
    }

    const [initialPostLocation, setInitialPostLocation] = useState<
        LocationType | undefined
    >();
    const [postGroup, setPostGroup] = useState<PostGroupType>();

    const { isOpen } = useCommentModalState();

    const [headerComponent, setHeaderComponent] = useState<ReactJSXElement>();

    const getModalTitle = useCallback(() => {
        if (headerComponent !== undefined) {
            return headerComponent;
        }

        return (
            <CardHeaderPostHeader
                avatar={
                    <Skeleton
                        animation="wave"
                        variant="circular"
                        width={40}
                        height={40}
                    />
                }
                title={
                    <TypographyHeaderSubTitle>
                        <Skeleton animation="wave" width="128px" />
                    </TypographyHeaderSubTitle>
                }
                subheader={
                    <TypographyHeaderSubTitle>
                        <Skeleton animation="wave" width="64px" />
                    </TypographyHeaderSubTitle>
                }
            />
        );
    }, [headerComponent]);

    return (
        <>
            <ModalConstructor
                onClose={handleClose}
                open={isPostDetailsModalOpen}
                modalTitle={!fullScreen && getModalTitle()}
                scrollableContainerRef={scrollableContainerRef}
                modalContent={
                    <>
                        <input
                            ref={focusCatcherInputRef}
                            style={{
                                position: "fixed",
                                height: 0,
                                width: 0,
                                top: -300,
                            }}
                        />
                        <Stack
                            sx={{
                                padding: "0 12px 12px 12px",
                                overflow: "hidden",
                            }}
                        >
                            <Box>
                                {Object.keys(post).length > 0 ? (
                                    <PostsWrapper
                                        openCommentInputModal={
                                            openCommentInputModal
                                        }
                                        posts={[post.data]}
                                        onPostEdit={onPostEdit}
                                        showShareOption={true}
                                        listType={
                                            fullScreen
                                                ? POST_LIST_TYPES.POST_DETAILS_FULLSCREEN
                                                : POST_LIST_TYPES.POST_DETAILS
                                        }
                                        updatePostComponent={
                                            updatePostComponent
                                        }
                                        setInitialPostLocation={
                                            setInitialPostLocation
                                        }
                                        openPostFormHandler={
                                            openPostFormHandler
                                        }
                                        setHeaderComponent={setHeaderComponent}
                                        handleCloseForHeaderComponent={
                                            handleClose
                                        }
                                        autoPlayVideo={
                                            postDetailsAction?.desiredAction ===
                                            PostDetailsPageActions.AUTO_PLAY_VIDEO
                                        }
                                        updatePostFromServer={() => {
                                            if (!selectedPostId) {
                                                return;
                                            }
                                            updatePostFromServer(
                                                selectedPostId,
                                                (
                                                    id,
                                                    stringPostId,
                                                    postData,
                                                ) => {
                                                    updatePostComponent({
                                                        ...postData,
                                                        pageRelated: {
                                                            boxed: false,
                                                            showPreviewComment:
                                                                false,
                                                            showCommentButton:
                                                                false,
                                                            hideImages: false,
                                                            hideActions: false,
                                                            wrapperMarginBottomValue:
                                                                "16px",
                                                        },
                                                    });
                                                    publish({
                                                        name: POST_DETAILS_CHANGED,
                                                        data: postData,
                                                    });
                                                },
                                            );
                                        }}
                                    />
                                ) : (
                                    <SkeletonPost
                                        isBoxed={false}
                                        isPostDetails={true}
                                    />
                                )}
                            </Box>
                            {pinnedComment && (
                                <Box ref={pinnedCommentRef}>
                                    <PostCardDivider />
                                    <PinnedCommentsBlock
                                        pinnedComment={pinnedComment}
                                        setReplyComment={onReplyToComment}
                                        onDeleteSuspendSuccess={
                                            onDeleteSuspendCommentSuccess
                                        }
                                        onEdit={onCommentEdit}
                                        updatePinnedComment={
                                            updatePinnedComment
                                        }
                                        onRepliedCommentClick={
                                            onRepliedCommentClick
                                        }
                                        isLatestComment={
                                            pinnedComment.id ===
                                            post.data.latestComment?.id
                                        }
                                    />
                                </Box>
                            )}
                            <PostCardDivider />
                            <Box ref={onCommentSubmitScrollToRef}>
                                {comments?.filter(
                                    (item) =>
                                        item.commentContent.id !==
                                        pinnedCommentId,
                                ).length > 1 && (
                                    <SortingChipsContainer
                                        style={{ margin: "8px 0 16px" }}
                                    >
                                        <SortingChips
                                            selectedIndex={sortingItemIndex}
                                            onChange={onSortingItemChange}
                                        />
                                    </SortingChipsContainer>
                                )}
                                <RefreshContentButton
                                    onClick={() => {
                                        setShouldRefresh(true);
                                    }}
                                    isInsideModal={!fullScreen}
                                    scrollableContainerRef={
                                        scrollableContainerRef
                                    }
                                />
                                <Box>
                                    <CommentsBlock
                                        comments={comments?.filter(
                                            (item) =>
                                                item.commentContent.id !==
                                                pinnedCommentId,
                                        )}
                                        setReplyComment={onReplyToComment}
                                        onDeleteSuspendSuccess={
                                            onDeleteSuspendCommentSuccess
                                        }
                                        onEdit={onCommentEdit}
                                        post={post}
                                        updateComments={updateComments}
                                        onCommentButtonClick={
                                            openCommentInputModal
                                        }
                                        pinnedCommentId={pinnedCommentId}
                                        isCommentsLoading={loading}
                                        onRepliedCommentClick={
                                            onRepliedCommentClick
                                        }
                                    />
                                    <ManualLoadingInfiniteScroller
                                        infiniteRef={infiniteRef}
                                        loadMore={loadMore}
                                        isLoading={loading}
                                        showLoadMoreButton={
                                            batchesLoaded %
                                                BATCHES_LOADED_THRESHOLD ===
                                            0
                                        }
                                        isInitLoadingDone={isInitDone}
                                        hasNextPage={hasNextPage}
                                        loadingMoreButtonText={"Mehr anzeigen"}
                                        loadMoreMargin={"0 0 32px 0"}
                                        autoLoadDataAfterClick={false}
                                        errorHappened={!!error}
                                    />
                                </Box>
                                {isSearchFallbackVisible &&
                                    post?.data?.group?.id &&
                                    post?.data?.status?.isQuestion === true &&
                                    post?.data?.postData?.content && (
                                        <SearchFallback
                                            excludePostId={post?.id}
                                            groupId={post.data.group.id}
                                            searchQuery={
                                                post?.data?.postData?.content
                                            }
                                        />
                                    )}
                            </Box>
                        </Stack>
                    </>
                }
                modalActions={
                    <>
                        <CommentInput
                            openCommentInputModal={openCommentInputModal}
                        />
                        {isOpen && selectedPostId && (
                            <CommentFormModal
                                postId={selectedPostId}
                                commentFormSubmissionCallback={
                                    onCommentFormSubmit
                                }
                                replyComment={replyComment}
                                clearReplyComment={clearReplyComment}
                                editedComment={selectedComment.current}
                                clearEditedComment={clearEditedComment}
                                sourceType={CommentSourceTypes.POST_DETAILS}
                            />
                        )}
                    </>
                }
                isBorderRadiusVisible={!fullScreen}
                showModalActionsDivider={false}
                modalInnerContainerStyles={{
                    overflowY: "unset",
                    height: fullScreen ? "100vh" : "calc(100vh - 24px)",
                    maxWidth: "640px",
                    "@media (min-width: 640px)": {
                        maxWidth: "640px",
                        borderRadius: "12px",
                    },
                    "& .MuiDialogContent-root": {
                        padding: "0",
                    },
                    "& .MuiDialogActions-root": {
                        justifyContent: "flex-start",
                        "& > :not(:first-of-type)": {
                            marginLeft: 0,
                        },
                    },
                    "& .MuiDialogTitle-root": {
                        margin: 0,
                        padding: "12px",
                        borderBottom:
                            post.data?.postData.type === POST_TYPES.TIPP_GAME
                                ? "none"
                                : "1px solid var(--community-color-dark-gray)",
                    },
                }}
            />
            {(selectedPost.data?.group?.id || postGroup?.id) &&
                isPostDetailsModalOpen && (
                    <PostFormModal
                        postFormSubmissionCallback={onPostFormSubmit}
                        data={selectedPost.data}
                        postFormModalCloseCallback={postFormModalCloseCallback}
                        initialUserLocation={initialPostLocation}
                        setInitialUserLocation={setInitialPostLocation}
                    />
                )}
        </>
    );
}
